import { CalendarDays, Clock, Download, GraduationCap, Info } from 'lucide-react';
import React, { Dispatch, FC, SetStateAction, Suspense, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { getLastUserAction } from '../../graphQLOperations/UserAction';
import { UserActionLastActionQuery } from '../../graphQLOperations/__generated__/UserActionLastActionQuery.graphql';
import { format } from 'date-fns';
import { BreadCrumb } from 'primereact/breadcrumb';
import { useNavigate } from 'react-router-dom';
import { getUserTimePlataform } from '../../graphQLOperations/IndividualStudentReport';
import { IndividualStudentReportUserTimeQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportUserTimeQuery.graphql';
import { Button } from 'primereact/button';
import { IndividualStudentReportPdfModal } from './IndividualStudentReportPdfModal';
import { StudentInfoInterface } from './IndividualReportWrapper';
import { IndividualReportStudentList } from './IndividualReportStudentList';
import { convertDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { Tooltip } from 'primereact/tooltip';

import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { useChildPageTitle } from '../../custom-hooks/useChildPageTitle';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';
import { PiiTypes } from '../../analytics/dataMasking';

const headerElements = [
  { id: 'class', label: 'Class', icon: <GraduationCap size={20} /> },
  { id: 'lastLogin', label: 'Last Login', icon: <CalendarDays size={20} /> },
  { id: 'time', label: 'Platform time today', icon: <Clock size={20} /> },
];

interface Props {
  id: string;
  setIndividualStudentId: Dispatch<SetStateAction<string | null>>;
  studentInfo?: StudentInfoInterface | null;
  allStudents:
    | {
        readonly nodes:
          | readonly (
              | {
                  readonly firstName: string | null | undefined;
                  readonly id: string;
                  readonly lastName: string | null | undefined;
                  readonly publicUserId: string | null | undefined;
                }
              | null
              | undefined
            )[]
          | null
          | undefined;
      }
    | null
    | undefined;
}

export interface IndividualModalValues {
  visible: boolean;
  usersId: string[] | null;
  userList: { id: string; firstName: string; lastName: string; publicUserId: string }[];
}
const dateQuery = new Date().toISOString();
export const IndividualStudentHeader: FC<Props> = ({ id, setIndividualStudentId, studentInfo, allStudents }) => {
  const sideNavActive = useSideNavActive();

  useChildPageTitle(studentInfo ? `${studentInfo.firstName} ${studentInfo.lastName}` : null, studentInfo?.publicUserId);
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const [modalValues, setModalValues] = useState<IndividualModalValues>({
    visible: false,
    usersId: null,
    userList: [],
  });

  const { allUserActions } = useLazyLoadQuery<UserActionLastActionQuery>(
    getLastUserAction,
    {
      id: `UserNode:${splitId(id)}`,
      action: ['ActionTypeNode:1'],
    },
    { fetchPolicy: 'network-only' },
  );

  const { getUserTimeOnPlatform } = useLazyLoadQuery<IndividualStudentReportUserTimeQuery>(
    getUserTimePlataform,
    {
      userId: `UserNode:${splitId(id)}`,
      date: dateQuery,
    },
    { fetchPolicy: 'network-only' },
  );

  const headerInfo: { [key: string]: string | undefined } = {
    class: studentInfo?.userClasses
      ?.filter(uc => uc?.isActive)
      .map(cl => `${cl?.class?.name}${cl?.class?.section ? ' - ' + cl?.class?.section : ''}`)
      .join(', '),
    lastLogin: allUserActions?.nodes?.length
      ? format(
          convertDateByUserTimezone(allUserActions.nodes[0].timeStamp, userRedux?.activeTimeZone || ''),
          'Pp',
        ).replace(/\//g, '-')
      : '-',
    time: String(getUserTimeOnPlatform.totalMinutes) + ' m',
  };

  const home = { icon: 'nc_icon-nc_home icon-sm', command: () => navigate('/') };
  const breadcrumbItems = [
    { label: 'Monitor', command: () => navigate('/quick-data') },
    {
      label: 'Progress',
      command: () => navigate('/progress'),
    },
    {
      label: 'Individual report',
      // span wrapping required to preserve whitespace because parent element style sets display:flex;
      template: (
        <span>
          <MaskedStudentData student={studentInfo!} type={PiiTypes.FullName}>
            {`${studentInfo?.firstName} ${studentInfo?.lastName}`}
          </MaskedStudentData>{' '}
          Individual report
        </span>
      ),
    },
  ];
  return (
    <Suspense>
      <div className="tw-flex tw-flex-col tw-gap-y-3 tw-my-3">
        {!sideNavActive && <BreadCrumb model={breadcrumbItems} home={home} />}
        <div className="tw-full tw-h-[3rem] tw-mt-4 tw-border-b-[4px] tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b-[#F1F0EE]">
          <Suspense>
            <IndividualReportStudentList setStudentId={setIndividualStudentId} id={id} allStudents={allStudents} />
          </Suspense>
        </div>
        <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
          {headerElements.map(item => (
            <React.Fragment key={item.id}>
              <div
                key={item.id}
                className="tw-w-[20%] tw-h-[2rem] tw-flex tw-items-center tw-border-[1px] tw-py-1 tw-px-3 tw-border-solid tw-border-neutral-300 tw-rounded-lg tw-bg-neutral-0"
              >
                <span className="tw-text-neutral-600 tw-h-[20px]">{item.icon}</span>
                <span className="tw-ml-2 tw-text-neutral-600 tw-min-w-fit">{item.label}:</span>
                <span className="tw-ml-1 tw-font-semibold tw-truncate tw-w-[100%]">{headerInfo[item.id]}</span>
              </div>
              {headerInfo[item.id] == '0 m' && (
                <>
                  <Info className="tooltip-time tw-text-blue-600" size={20} />
                  <Tooltip position="bottom" target=".tooltip-time">
                    <div>
                      <p className="tw-text-white tw-px-3">
                        <b>No recorded platform time yet.</b>
                      </p>
                      <p className="tw-text-white tw-px-3">
                        {`Platform time reflects active learning like assessments and
                      lessons.`}
                      </p>
                      <p className="tw-text-white tw-px-3">{`Some activities (e.g., Diagnostics) aren't included. Time will appear once qualifying
                      actions happen.`}</p>
                    </div>
                  </Tooltip>
                </>
              )}
            </React.Fragment>
          ))}
          <div style={{ fontFamily: 'Poppins' }} className=" tw-w-[18%] tw-px-4">
            <p className="tw-text-xs tw-text-neutral-600 tw-text-end tw-pb-0 tw-mb-0">Last update:</p>
            <p className="tw-font-semibold tw-text-xs tw-pt-0 tw-text-end tw-mt-1">{format(new Date(), 'Pp')}</p>
          </div>

          <Button
            onClick={() => {
              setModalValues({
                visible: true,
                usersId: [id],
                userList:
                  allStudents?.nodes
                    ?.map(el => {
                      return {
                        id: el?.id ?? '',
                        firstName: el?.firstName ?? '',
                        lastName: el?.lastName ?? '',
                        publicUserId: el?.publicUserId ?? '',
                      };
                    })
                    .sort((a, b) => a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' })) ?? [],
              });
            }}
            className="tw-w-[18%]"
            icon={<Download className="xl:tw-ml-4" />}
            label="Download report"
          />
        </div>
        {modalValues.visible && (
          <Suspense>
            <IndividualStudentReportPdfModal modalValues={modalValues} setModalValues={setModalValues} />
          </Suspense>
        )}
      </div>
    </Suspense>
  );
};
